/**
 * `useStandbyActivation` — #2211 slice A: activate a standby onto a vacated
 * call (`POST /api/cycles/{id}/standby/activate`).
 *
 * Founder, 12 Oct 2026: *"Only real calls count. If someone is standby but
 * is activated, the roster gets adjusted in a way that they get the next day
 * off by default."* Dr Lee holds MO-SB on Thursday; Dr Tan, on MO1, is off
 * sick. Activating Dr Lee turns her standby row into MO1, removes Dr Tan's
 * call, holds the swap through a re-solve, tells them both, and answers with
 * the post-call rest offer (`rest_offer`, `kind: "call"`) — which the lead
 * confirms through `useConfirmRest` (her Friday ward seats come off).
 *
 * Used by the ladder's *Activate a standby* rung (laptop panel, phone sheet)
 * and by the grid's day editor (*Activate the standby* on an MO-SB row).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";
import { invalidateAfterRung } from "./useShortfallOptions";

export type StandbyCandidate = components["schemas"]["StandbyCandidate"];
export type ActivateStandbyResponse = components["schemas"]["ActivateStandbyResponse"];

export type ClearActivationResponse = components["schemas"]["ClearActivationResponse"];
export type GapCall = components["schemas"]["GapCall"];
export type NightStandby = components["schemas"]["NightStandby"];
export type KeepRestResponse = components["schemas"]["KeepRestResponse"];
export type RestGiven = components["schemas"]["RestGiven"];

export interface ActivateStandbyVars {
  date: string;
  membershipId: string;
  /** Case A: the vacated call they take (`MO1`). */
  callPost?: string;
  /** #2211 C — case C, "called in on top": no call is vacated; their
   *  standby is marked activated and rests them the next day. */
  onTop?: boolean;
}

export function useActivateStandby(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<ActivateStandbyResponse, ApiError | Error, ActivateStandbyVars>({
    mutationFn: ({ date, membershipId, callPost, onTop }) =>
      apiFetch<ActivateStandbyResponse>(`/api/cycles/${cycleId}/standby/activate`, {
        method: "POST",
        body: JSON.stringify(
          onTop
            ? { date, membership_id: membershipId, on_top: true }
            : { date, membership_id: membershipId, call_post: callPost },
        ),
      }),
    onSuccess: () => {
      invalidateAfterRung(qc, cycleId);
    },
  });
}

/** #2211 C — undo "called in on top": the standby's mark is cleared. */
export function useClearActivation(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    ClearActivationResponse,
    ApiError | Error,
    { date: string; membershipId: string }
  >({
    mutationFn: ({ date, membershipId }) =>
      apiFetch<ClearActivationResponse>(
        `/api/cycles/${cycleId}/standby/activate?date=${encodeURIComponent(
          date,
        )}&membership_id=${encodeURIComponent(membershipId)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      invalidateAfterRung(qc, cycleId);
    },
  });
}

/** #2211 (founder, 12 Oct) — the team asks each time and the post-call day
 *  is short: keep them on. The rest goes to the recall rule's day back later
 *  (the response's `offer`), or the offer's note says it was not given.
 *  `restCycleId` is the window that owns the kept day. */
export function useKeepRest(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    KeepRestResponse,
    ApiError | Error,
    { membershipId: string; callDate: string; date: string; restCycleId?: string | null }
  >({
    mutationFn: ({ membershipId, callDate, date, restCycleId }) =>
      apiFetch<KeepRestResponse>(`/api/cycles/${restCycleId ?? cycleId}/standby/rest/keep`, {
        method: "POST",
        body: JSON.stringify({ membership_id: membershipId, call_date: callDate, date }),
      }),
    onSuccess: (_res, vars) => {
      invalidateAfterRung(qc, cycleId);
      if (vars.restCycleId && vars.restCycleId !== cycleId) {
        invalidateAfterRung(qc, vars.restCycleId);
      }
    },
  });
}

export type CoverPreview = components["schemas"]["CoverPreview"];

/** #2211 review — before Save of *covered by* on a call row: what would come
 *  off the coverer's next day. Plain fetch (the dialog lives outside any
 *  query client in some surfaces); `null` when it cannot be read. */
export async function fetchCoverPreview(
  cycleId: string,
  assignmentId: string,
  membershipId: string,
): Promise<CoverPreview | null> {
  try {
    return await apiFetch<CoverPreview>(
      `/api/cycles/${cycleId}/standby/cover-preview?assignment_id=${encodeURIComponent(
        assignmentId,
      )}&membership_id=${encodeURIComponent(membershipId)}`,
    );
  } catch {
    return null;
  }
}

/** #2284 — before the lead confirms *Called in on top* (the day editor has
 *  no ladder candidate to carry it): the person's other calls inside the gap
 *  window of the night. `callPost` reads the window of a swap-in onto that
 *  call instead. The ladder's rung carries the same list on each candidate
 *  (`StandbyCandidate.gap_calls`). */
export function useStandbyGapCalls(
  cycleId: string,
  date: string,
  membershipId: string,
  { callPost, enabled = true }: { callPost?: string; enabled?: boolean } = {},
) {
  return useQuery<GapCall[], ApiError | Error>({
    queryKey: ["standby-gap-calls", cycleId, date, membershipId, callPost ?? null],
    queryFn: async () => {
      const post = callPost ? `&call_post=${encodeURIComponent(callPost)}` : "";
      const res = await apiFetch<components["schemas"]["GapCallsPreview"]>(
        `/api/cycles/${cycleId}/standby/gap-calls?date=${encodeURIComponent(
          date,
        )}&membership_id=${encodeURIComponent(membershipId)}${post}`,
      );
      return res.gap_calls ?? [];
    },
    enabled: enabled && Boolean(cycleId && date && membershipId),
  });
}

/** #2284 — one line per call inside the gap window: *Dr Lee also holds MO3
 *  on Sat 17 Oct (2 days later). Re-solve these dates or move it.* A call
 *  already worked is said as such (nothing is left to move). An activated
 *  standby is not priced against this activation by the solve, so its line
 *  asks for a move by hand instead of a re-solve. */
export function gapCallLine(name: string, call: GapCall): string {
  const day = restDayLabel(call.date);
  const label = call.label || call.post;
  const post = call.activated_standby ? `${label} (activated)` : label;
  const away = `${call.days_away} day${call.days_away === 1 ? "" : "s"} ${
    call.later ? "later" : "earlier"
  }`;
  if (call.past) return `${name} held ${post} on ${day} (${away}); that call is already worked.`;
  if (call.activated_standby) {
    return `${name} also holds ${post} on ${day} (${away}): both are activations; move one by hand.`;
  }
  return `${name} also holds ${post} on ${day} (${away}). Re-solve these dates or move it.`;
}

export function gapCallLines(name: string, calls: readonly GapCall[] | null | undefined): string[] {
  return (calls ?? []).map((c) => gapCallLine(name, c));
}

/** `Fri 16 Oct` — the one day form every standby line and button uses. */
export function restDayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** `Fri 16 Oct off · released from 56 AM, 56 PM` — a day given as part of
 *  the activation (the team gives the whole next day off). */
export function restGivenWords(given: readonly RestGiven[] | null | undefined): string | null {
  if (!given || given.length === 0) return null;
  return given
    .map((g) => {
      const day = restDayLabel(g.date);
      const seats = g.released ?? [];
      return seats.length > 0 ? `${day} off · released from ${seats.join(", ")}` : `${day} off`;
    })
    .join(" · ");
}

/** The "Keep on 56 AM — the day is short" button's words. */
export function keepLabel(released: readonly string[] | null | undefined): string {
  const seats = released ?? [];
  return `Keep on ${seats.length > 0 ? seats.join(", ") : "that day"} — the day is short`;
}

/** `MO-SB → MO1` — the activation in the lead's words, for a notice. */
export function activationNotice(res: ActivateStandbyResponse, name: string, day: string): string {
  const replaced = (res.replaced ?? []).map((r) => r.display_name).filter(Boolean);
  const owed = res.rest_offer?.days_owed ?? 0;
  const given = restGivenWords(res.rest_given);
  // #2211 (founder, 12 Oct) — given now (the team's default), or still owed.
  const restWords = given
    ? given
    : owed > 0
      ? `${owed} rest day${owed === 1 ? "" : "s"} owed`
      : null;
  // #2284 — the calls inside the gap window, said again after the write.
  const gap = gapCallLines(name, res.gap_calls);
  const withGap = (text: string) => (gap.length > 0 ? `${text}. ${gap.join(" ")}` : text);
  if (res.mode === "on_top") {
    return withGap(
      [
        `${name} is called in on top on ${day} · their ${res.standby_post} is marked activated`,
        "they will be told",
        restWords,
      ]
        .filter(Boolean)
        .join(" · "),
    );
  }
  const parts = [
    `${name} is on ${res.call_post} on ${day} · moved from ${res.standby_post}`,
    replaced.length > 0 ? `${replaced.join(", ")} comes off the call` : null,
    "they will be told",
    restWords,
  ];
  return withGap(parts.filter(Boolean).join(" · "));
}
